import React, { useRef, useState, useEffect, memo } from 'react';
import Link from 'redux-first-router-link';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import listAnalytics from './listAnalytics';
import TemplateParser from '../../components/Templates/TemplateParser';
import { updateFilter, rebaseFilter } from '../../actions/environment';
import { unique, arraysEqual } from '../../utils/array';
import { addEvent, removeEvent, stickyOffset, offset, scrollTo } from '../../utils/dom';
import { translate, prefix } from '../../utils/locale';
import { urlify } from '../../utils/string';
import s from './Pagination.scss';

const areEqual = (prevProps, nextProps) => {
	if (
		prevProps.list.length === nextProps.list.length
		&& prevProps.currentPath === nextProps.currentPath
		&& prevProps.isFiltered === nextProps.isFiltered
		&& arraysEqual(prevProps.list, nextProps.list)
	) return true;
	return false;
};

const Pagination = ({
	ListComponent,
	collectionSections = {},
	variantClasses = [],
	categoryTitles = {},
	styleFilter = {},
	productPickerFilter = [],
	productPickerFilterType = '',
	location,
	setup,
	filteredBy,
	list,
	cache,
	currentPath,
	currentView,
	isFiltered = false,
	currentHash = '',
	productPicker = false,
	canonical = false,
	magazine = false,
	filterOpen = false,
	closeSearch = () => {},
	layout = 'default',
	...props
}) => {
	const paginationContainer = useRef(null);
	const pageBase = unique(list || cache);
	const [stickySection, updateStickySection] = useState(-1);
	const hasHeaders = location.type !== 'SEARCH' && currentView !== 'related' && !productPicker;

	useEffect(() => {
		const shouldUpdateStickyHeadings = () => {
			if (!paginationContainer?.current) return;
			const top = window.pageYOffset || document.documentElement.scrollTop;
			if (top === 0 && currentSection !== -1) updateStickySection(-1);
			const $sections = paginationContainer.current.getElementsByTagName('section');
			const currentSection = [...$sections].reduce((acc, $s, i) => {
				if ((setup.breakPoint !== 'mobile' && stickyOffset($s.querySelector('h1')).top === 50) || (setup.breakPoint === 'mobile' && stickyOffset($s.querySelector('h1')).top === 0)) {
					return i;
				}
				return acc;
			}, -1);
			if (currentSection !== stickySection) updateStickySection(currentSection);
		};

		addEvent(window, 'scroll.stickyHeadings', () => {
			window.requestAnimationFrame(shouldUpdateStickyHeadings);
		});

		return () => {
			removeEvent(window, 'scroll.stickyHeadings');
		};
	}, [stickySection, paginationContainer, setup.breakPoint]);

	const collections = pageBase.reduce((acc, item) => {
		const tempItem = cache[item];
		if (!tempItem) return acc;

		// Non heirarchial
		if (!/[A-Z]\d{3}/.test(tempItem.name) || !hasHeaders || currentPath.indexOf('jeans') === -1 || isFiltered || /^\/sale/.test(currentPath)) {
			if (!acc.general) acc.general = [];
			acc.general.push(item);
			return acc;
		}


		// Heirarchial
		const tempCollection = urlify(tempItem.name);
		if (!acc[tempCollection]) acc[tempCollection] = [];
		acc[tempCollection].push(item);
		return acc;
	}, {});

	const handleFilterUpdate = (e) => {
		e.preventDefault();
		const $link = e.currentTarget;
		const action = $link.getAttribute('href');
		const fT = action.indexOf('#') === 0 ? action.slice(1, action.indexOf('=')) : 'categories';
		const fV = action.indexOf('#') === 0 ? action.slice(action.indexOf('=') + 1) : action.replace(prefix(setup, ''), '');

		if (productPickerFilter.length > 0) {
			setTimeout(() => {
				window.scrollTo(0, offset($link).top - 130);
			}, 1);
		}

		if (fV) props.updateFilter([{ [fT]: fV }], false, setup, currentPath);
		else props.rebaseFilter(setup, currentPath);
	};

	let paginationClasses = [s.pagination];
	if (filterOpen) paginationClasses.push(s.pagination__filterOpen);
	if (pageBase.length < 5) paginationClasses.push(s.pagination__centered);
	if (variantClasses) paginationClasses = [...paginationClasses, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c && c.indexOf('pagination__') !== -1).map((c) => s[c]) : [s[variantClasses.toString()]])];

	const itemsShowing = [];
	const linkClasses = [s.links];
	let filterLinks = [];
	let filterType = 'styles';
	let allLink = currentPath.slice(1).split('/').slice(0, 2).join('/');
	if (hasHeaders && /\/jeans/.test(currentPath)) {
		allLink = '#styles=';
		filterLinks = [...styleFilter];
		linkClasses.push(s.links__sticky);
	}
	if (productPickerFilter.length > 0) {
		allLink = `#${productPickerFilterType}=`;
		filterLinks = [...productPickerFilter];
		filterType = productPickerFilterType;
		linkClasses.push(s.links__productPicker);
	}

	const collectionListing = Object.keys(collections).map((collection, i) => {
		let collectionCount = 0;
		let skipProducts = 0;
		let subComponent = [];
		let firstItem = {};
		const listing = collections[collection].sort((a, b) => {
			const aItem = list ? cache[a] : a;
			const bItem = list ? cache[b] : b;
			if (aItem.inStock === 0 && !aItem.variant_coming_soon_bool) return 1;
			if (bItem.inStock === 0 && !bItem.variant_coming_soon_bool) return -1;
			return 0;
		}).map((item) => {
			const id = item;
			let items = false;
			if (list) {
				item = cache[id];
				if (!item) items = Object.keys(cache).filter((cacheID) => cacheID.indexOf(id) !== -1);
			}
			firstItem = item;
			const component = [];

			if (collectionSections?.[collection]?.[collectionCount]) {
				const inlineCategoryContentTemplate = <TemplateParser key={`content_${collectionCount}`} sections={collectionSections[collection][collectionCount].sections} setup={setup} inline inlineCategoryContent />;
				component.push(inlineCategoryContentTemplate);

				collectionCount += 1;
			}
			if (skipProducts === 0) subComponent = [];

			if (items) {
				items.forEach((variantID) => {
					const variant = cache[variantID];
					itemsShowing.push(variant);
					component.push(<ListComponent variantClasses={[...variantClasses, skipProducts > 0 ? 'productItem__inlineCategoryContent' : null]} key={variantID} id={variantID} content={variant} canonical={magazine ? translate(variant.uri, setup.language).text : canonical} setup={setup} currentPath={currentPath} currentView={currentView} itemIndex={collectionCount + 2} currentHash={currentHash} closeSearch={closeSearch} layout={layout} />);
					collectionCount += 1;
				});
			} else if (item) {
				itemsShowing.push(item);
				component.push(<ListComponent variantClasses={[...variantClasses, skipProducts > 0 ? 'productItem__inlineCategoryContent' : null]} key={list ? id : urlify(translate(item.uri, setup.language).text)} id={id} content={item} canonical={magazine ? translate(item.uri, setup.language).text : canonical} setup={setup} currentPath={currentPath} currentView={currentView} itemIndex={collectionCount + 2} currentHash={currentHash} closeSearch={closeSearch} layout={layout} />);
				collectionCount += 1;
			}

			// Products included in CMS template.
			if (skipProducts > 0) {
				skipProducts -= 1;
				collectionCount += 1;
				subComponent = [...subComponent, ...component];
				return skipProducts === 0 ? <div key={collectionCount} className={[s.contentProductCombo, s.contentProductCombo__left].join(' ')}>{ subComponent }</div> : null;
			}
			return component;
		});

		let categoryLinks = [];
		if (hasHeaders && /^\/sale/.test(currentPath)) {
			let testPath = currentPath.slice(1);
			const splitSlashes = testPath.split('/');
			if (splitSlashes.length > 2) testPath = testPath.slice(0, currentPath.lastIndexOf('/') - 1);
			categoryLinks = Object.keys(categoryTitles).filter((category) => (
				category.indexOf(testPath) === 0 && category !== testPath && category.split('/').length - 1 <= splitSlashes.length
			)).map((link) => ({ uri: link, title: categoryTitles[link] }));
		}

		return (
			<section key={collection} className={[s.pagination_collection, (i === stickySection ? s.pagination_collection__sticky : null)].join(' ')}>
				{
					hasHeaders && (
						<h1 className={s.pagination_collection_title}>
							{ collection === 'general' ? categoryTitles[currentPath.slice(1)] : firstItem.name }
							{
								categoryLinks.length > 0 && (
									<div className={s.links}>
										<Link to={prefix(setup, allLink)} className={[s.link, allLink === currentPath.slice(1) ? s.link__selected : null].join(' ')}>{ translate(setup, 'all') }</Link>
										{
											categoryLinks.map((link) => {
												const isSelected = currentPath.slice(1) === link.uri;
												return <Link key={link.uri} className={[s.link, isSelected ? s.link__selected : null].join(' ')} to={prefix(setup, isSelected ? link.uri.slice(0, link.uri.lastIndexOf('/')) : link.uri)}>{ link.title }</Link>;
											})
										}
									</div>
								)
							}
						</h1>
					)
				}
				{ listing }
			</section>
		);
	});


	// Change to only send products which are visible.
	// Batch send them.
	// Clear queue
	if (itemsShowing.length > 0) {
		listAnalytics(itemsShowing, setup, currentView);
	}

	return (
		<div className={paginationClasses.join(' ')} ref={paginationContainer}>
			{
				filterLinks.length > 0 && (
					<div className={linkClasses.join(' ')}>
						<a href={allLink} onClick={handleFilterUpdate} className={[s.link, filteredBy.filter((fT) => Object.keys(fT)[0] === filterType).length === 0 ? s.link__selected : null].join(' ')}>{ translate(setup, 'all') }</a>
						{
							filterLinks.map((link) => {
								const isSelected = filteredBy.filter((fT) => {
									const type = Object.keys(fT)[0];
									return fT[type].reduce((acc, value) => (`#${type}=${value}` === link.uri ? 1 : acc), 0);
								}).length > 0;
								return <a key={link.uri} href={link.uri} onClick={handleFilterUpdate} className={[s.link, isSelected ? s.link__selected : null].join(' ')}>{ link.title }</a>;
							})
						}
					</div>
				)
			}
			{ collectionListing }
		</div>
	);
};

const mapState = ({ environment, location, filter }, { showAll = false, paging = null, currentHash = '', canonical = false, magazine = false, ...props }) => ({
	pagination: environment.pagination,
	setup: environment.setup,
	filteredBy: filter.filteredBy,
	location,
	showAll,
	paging,
	currentHash,
	canonical,
	magazine,
	...props,
});

export default hot(module)(connect(mapState, { updateFilter, rebaseFilter })(memo(Pagination, areEqual)));
